<script setup lang="ts">
import { Roles } from '@/constants'
import type {
  FeedbacksAdminModel,
  TestPersonalInformationAdminModel,
  TestsAdminShortModel,
  UserModel,
} from '@/services/http/httpResponses'
import usersHttpService from '@/services/http/usersHttpService'
import { computed, ref } from 'vue'
import { BillStatuses, type Id } from '@raildev/shared-utils'

definePage({
  meta: { allowedRoles: [Roles.Admin, Roles.CareerGuidanceSpecialist] },
})

const filterParams = ref({
  name: null as string | null,
  email: null as string | null,
})

const users = ref<UserModel[]>([])

const refreshUsers = async () => {
  const res = await usersHttpService.getUsers({
    role: Roles.User,
    name: filterParams.value.name,
    email: filterParams.value.email,
  } as { role: Roles })
  users.value = res.users
}

const formattedUsers = computed(() =>
  users.value.map((u) => ({
    id: u.id,
    name: u.name ?? '-',
    email: u.email ?? '-',
    phoneNumber: u.phoneNumber ?? '-',
    createdAt: (() => {
      if (!u.createdAt) return '-'
      const d = new Date(u.createdAt)
      if (d.getFullYear() <= 1) return '-'
      return d.toLocaleString('ru-RU', { hour12: false })
    })(),
    activated: u.emailConfirmed || u.phoneNumberConfirmed,
    startedTestsCount: u.startedTestsCount,
    completedTestsCount: u.completedTestsCount,
    paidTestsCount: u.paidTestsCount,
  })),
)

const showViewDialog = ref(false)
const activeTab = ref('profile')
const selectedUser = ref<UserModel | null>(null)
const personalInfo = ref<TestPersonalInformationAdminModel | null>(null)
const userTests = ref<TestsAdminShortModel[]>([])
const userFeedbacks = ref<FeedbacksAdminModel[]>([])
const viewLoading = ref(false)

const openViewDialog = async (row: { id: Id }, tab = 'profile') => {
  activeTab.value = tab
  selectedUser.value = users.value.find((x) => x.id === row.id) ?? null
  personalInfo.value = null
  userTests.value = []
  userFeedbacks.value = []
  showViewDialog.value = true
  viewLoading.value = true
  try {
    const [piRes, testsRes, feedbacksRes] = await Promise.all([
      usersHttpService.getPersonalInfoByUserId(row.id),
      usersHttpService.getTestsByUserId(row.id),
      usersHttpService.getFeedbacksByUserId(row.id),
    ])
    personalInfo.value = piRes.user ?? null
    userTests.value = testsRes.tests
    userFeedbacks.value = feedbacksRes.feedbacks
  } finally {
    viewLoading.value = false
  }
}

const testStatusLabel = (status: number) => {
  const map: Record<number, string> = {
    0: 'Не создан',
    1: 'Создан',
    2: 'Генерация вопросов',
    3: 'Начат',
    4: 'Генерация результатов',
    5: 'Завершён',
  }
  return map[status] ?? String(status)
}

await refreshUsers()
</script>

<template>
  <section class="flex flex-col w-full">
    <Toast />
    <div class="flex gap-4 mb-4 justify-between items-end sm:items-center">
      <div class="grow flex gap-3 flex-col items-start sm:flex-row sm:items-center">
        <p>Фильтровать по</p>
        <FloatLabel class="max-w-56 w-full" variant="on">
          <InputText class="max-w-56 w-full" id="name-input" v-model="filterParams.name" />
          <label for="name-input">Имя</label>
        </FloatLabel>
        <FloatLabel class="max-w-56 w-full" variant="on">
          <InputText class="max-w-56 w-full" id="email-input" v-model="filterParams.email" />
          <label for="email-input">Почта</label>
        </FloatLabel>
      </div>
      <Button label="Применить" @click="refreshUsers" />
    </div>

    <DataTable :value="formattedUsers" dataKey="id">
      <Column field="name" header="Имя" />
      <Column field="email" header="Email" />
      <Column field="phoneNumber" header="Телефон" />
      <Column field="createdAt" header="Дата регистрации" />
      <Column header="Начатых тестов">
        <template #body="slotProps">
          <button @click="openViewDialog(slotProps.data, 'tests')">
            <Tag
              :value="String(slotProps.data.startedTestsCount)"
              :severity="slotProps.data.startedTestsCount > 0 ? 'success' : 'danger'"
            />
          </button>
        </template>
      </Column>
      <Column header="Завершённых тестов">
        <template #body="slotProps">
          <button @click="openViewDialog(slotProps.data, 'tests')">
            <Tag
              :value="String(slotProps.data.completedTestsCount)"
              :severity="slotProps.data.completedTestsCount > 0 ? 'success' : 'danger'"
            />
          </button>
        </template>
      </Column>
      <Column header="Оплаченных тестов">
        <template #body="slotProps">
          <button @click="openViewDialog(slotProps.data, 'tests')">
            <Tag
              :value="String(slotProps.data.paidTestsCount)"
              :severity="slotProps.data.paidTestsCount > 0 ? 'success' : 'danger'"
            />
          </button>
        </template>
      </Column>
      <Column header="Статус активации">
        <template #body="slotProps">
          <Tag
            :value="slotProps.data.activated ? 'Активирован' : 'Не активирован'"
            :severity="slotProps.data.activated ? 'success' : 'warn'"
          />
        </template>
      </Column>
      <Column header="Действия">
        <template #body="slotProps">
          <Button label="Просмотр" @click="openViewDialog(slotProps.data)" />
        </template>
      </Column>
      <template #empty>
        <div class="flex flex-col gap-4 items-center">
          <span class="pi pi-search text-6xl" />
          <h3 class="font-medium">По выбранным фильтрам данные не найдены</h3>
        </div>
      </template>
    </DataTable>

    <Dialog
      v-model:visible="showViewDialog"
      :header="selectedUser?.name ?? selectedUser?.email ?? 'Клиент'"
      :modal="true"
      class="w-[95vw] sm:w-[700px]"
      :closable="true"
    >
      <ProgressSpinner v-if="viewLoading" class="flex justify-center" />
      <Tabs v-else v-model:value="activeTab">
        <TabList>
          <Tab value="profile">Профиль</Tab>
          <Tab value="tests">Тесты ({{ userTests.length }})</Tab>
          <Tab value="feedbacks">Отзывы ({{ userFeedbacks.length }})</Tab>
        </TabList>
        <TabPanels>
          <TabPanel value="profile">
            <div v-if="personalInfo" class="flex flex-col gap-4 pt-2">
              <section>
                <h3 class="font-semibold mb-1">Текущая активность</h3>
                <p>{{ personalInfo.currentActivity?.name ?? '—' }}</p>
              </section>
              <section v-if="personalInfo.interests?.length">
                <h3 class="font-semibold mb-1">Интересы</h3>
                <div class="flex gap-2 flex-wrap">
                  <Chip
                    v-for="i in personalInfo.interests"
                    :key="i.id"
                    :label="i.name"
                    class="bg-blue-200"
                  />
                </div>
              </section>
              <section v-if="personalInfo.hobbies?.length">
                <h3 class="font-semibold mb-1">Хобби</h3>
                <div class="flex gap-2 flex-wrap">
                  <Chip
                    v-for="h in personalInfo.hobbies"
                    :key="h.id"
                    :label="h.name"
                    class="bg-blue-200"
                  />
                </div>
              </section>
              <section v-if="personalInfo.subjects?.length">
                <h3 class="font-semibold mb-1">Дисциплины</h3>
                <div class="flex gap-2 flex-wrap">
                  <Chip
                    v-for="s in personalInfo.subjects"
                    :key="s.id"
                    :label="s.name"
                    class="bg-blue-200"
                  />
                </div>
              </section>
              <section v-if="personalInfo.personalCharacteristics?.length">
                <h3 class="font-semibold mb-1">Личностные характеристики</h3>
                <div class="flex gap-2 flex-wrap">
                  <Chip
                    v-for="c in personalInfo.personalCharacteristics"
                    :key="c.id"
                    :label="c.name"
                    class="bg-blue-200"
                  />
                </div>
              </section>
            </div>
            <p v-else class="text-gray-500 pt-2">Профиль не заполнен</p>
          </TabPanel>

          <TabPanel value="tests">
            <DataTable :value="userTests" dataKey="id" class="mt-2">
              <Column header="Дата начала">
                <template #body="slotProps">
                  {{ new Date(slotProps.data.startDateTime).toLocaleString('ru-RU') }}
                </template>
              </Column>
              <Column header="Статус">
                <template #body="slotProps">
                  {{ testStatusLabel(slotProps.data.status) }}
                </template>
              </Column>
              <Column header="Оплата">
                <template #body="slotProps">
                  <Tag
                    v-if="slotProps.data.billStatus != null"
                    :value="
                      slotProps.data.billStatus === BillStatuses.Success ? 'Оплачен' : 'Не оплачен'
                    "
                    :severity="
                      slotProps.data.billStatus === BillStatuses.Success ? 'success' : 'warn'
                    "
                  />
                  <span v-else>—</span>
                </template>
              </Column>
              <Column header="">
                <template #body="slotProps">
                  <RouterLink :to="`/test-analytics/${slotProps.data.id}`">
                    <Button label="Открыть" size="small" text />
                  </RouterLink>
                </template>
              </Column>
              <template #empty>
                <p class="text-gray-500 text-center py-4">Тестов нет</p>
              </template>
            </DataTable>
          </TabPanel>

          <TabPanel value="feedbacks">
            <DataTable :value="userFeedbacks" dataKey="id" class="mt-2">
              <Column header="Дата">
                <template #body="slotProps">
                  {{ new Date(slotProps.data.createdAt).toLocaleString('ru-RU') }}
                </template>
              </Column>
              <Column header="Оценка">
                <template #body="slotProps">
                  <Tag :value="`${slotProps.data.rating} / 5`" severity="info" />
                </template>
              </Column>
              <Column field="comment" header="Комментарий" />
              <template #empty>
                <p class="text-gray-500 text-center py-4">Отзывов нет</p>
              </template>
            </DataTable>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Dialog>
  </section>
</template>

<style scoped></style>
